import React, { useEffect, useState, Fragment } from 'react'
import { connect, useDispatch } from 'react-redux'
import addLineItem from 'base/actions/add_line_item'
import Button from '@everlane/button'
import Carousel from '@everlane/carousel'
import { Lock } from '@everlane/icon'
import { BuilderComponent } from '@builder.io/react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import Event from 'base/event'
import Hub from 'base/hub'
import { getCountryCode } from 'base/lib/localization'
import { returnPricingCopyFor } from 'base/lib/return_policy'
import ImageHelper from 'base/lib/image_helper'
import {
  updateItemQuantity,
  fetchProductUpsell,
  fetchOrderPreview,
  fetchProductPrices,
  fetchReturnPolicy,
} from 'base/actions/asynchronous'
import { receiveShowingFlyoutCart } from 'base/actions/synchronous'

import Modal from 'base/react/components/modal'
import Cart from 'desktop/components/checkout/cart'
import PurchaseButton from 'base/react/components/products/purchase_button'
import RouterLink from 'base/components/router_link'
import ShippingProgressBar from './shipping_progress_bar'

const FlyoutCartSubtotal = ({
  lineItems,
  hasWelcomeOffer,
  currencySymbol,
  subTotalDecimal,
  returnPolicy,
  cartUrgencyMessagingEnabled,
}) => {
  const totalLineItems = lineItems.reduce((total, lineItem) => total + lineItem.quantity, 0)

  const { return_pricing: returnPricing } = returnPolicy || {}

  const totalLineItemsMessage =
    totalLineItems === 1 ? `(${totalLineItems} Item)` : `(${totalLineItems} Items)`
  const afterPayTotal = subTotalDecimal / 4
  let subMessage
  if (currencySymbol === '$') {
    subMessage = `Or 4 installments of $${afterPayTotal.toFixed(2)} with Afterpay`
  } else if (returnPricing) {
    subMessage = returnPricingCopyFor(returnPricing)
  }

  return (
    <div
      className={classNames('flyout-cart__subtotal', {
        'flyout-cart__subtotal--urgency-messaging': cartUrgencyMessagingEnabled,
      })}
    >
      {hasWelcomeOffer && (
        <div className="flyout-cart__subtotal-discount">
          Credits and Discounts will be applied at Checkout
        </div>
      )}
      <h3 className="flyout-cart__subheader">
        Subtotal{' '}
        <span className="flyout-cart__subtotal-header-quantity">{totalLineItemsMessage}</span>
        <span className={'flyout-cart__subtotal-header-amount'}>
          {subTotalDecimal !== 0 && `${currencySymbol}${subTotalDecimal.toFixed(0)}`}
        </span>
      </h3>
      <h4 className="flyout-cart__submessage-copy">{subMessage}</h4>

      <div className="flyout-cart__buttons-container">
        <RouterLink href="/checkout">
          <Button
            dark
            type="button"
            variant="rectangle"
            className="flyout-cart__subtotal-continue-button"
            onClick={() => {
              Hub.pub(Event.Navigation.PREVIEW_CHECKOUT)
            }}
          >
            Continue to Checkout <Lock />
          </Button>
        </RouterLink>
      </div>
      {cartUrgencyMessagingEnabled && <BuilderComponent model="cart-urgency-messaging" />}
    </div>
  )
}

const FlyoutCart = props => {
  const {
    areLineItemsSyncing,
    lineItems,
    upsellProductGroups,
    addToCart,
    setFlyoutCartIsShowing,
    hasWelcomeOffer,
    currencySymbol,
    subTotalDecimal,
    orderTotalDecimal,
    closeCart,
    productPrices,
    returnPolicy,
    countryCode,
    cartUrgencyMessagingEnabled,
  } = props

  const dispatch = useDispatch()
  const dispatchUpdateItemQuantity = async (lineItem, newQuantity) => {
    await dispatch(updateItemQuantity(lineItem, newQuantity))
    await dispatch(fetchOrderPreview(null, { include_promos_in_subtotal: true }))
  }

  const [upsellProducts, setUpsellProducts] = useState([])
  const [hasUpsellProducts, setHasUpsellProducts] = useState(false)

  useEffect(() => {
    dispatch(
      fetchOrderPreview(null, {
        include_promos_in_subtotal: true,
        cart: true,
      }),
    )
    dispatch(fetchProductUpsell())
  }, [dispatch])

  useEffect(() => {
    if (countryCode) dispatch(fetchReturnPolicy(countryCode))
  }, [dispatch, countryCode])

  useEffect(() => {
    // disable body scroll when modal is open
    document.body.classList.add('locked')
    return () => {
      document.body.classList.remove('locked')
    }
  }, [])

  useEffect(() => {
    const dispatchFetchUpsellPrices = async productIds => {
      await dispatch(fetchProductPrices(productIds))
    }

    if (upsellProductGroups.length > 0 && upsellProducts.length === 0) {
      const upsells = upsellProductGroups.map(productGroup => productGroup.products[0])
      setUpsellProducts(upsells)
      setHasUpsellProducts(true)

      dispatchFetchUpsellPrices(upsells.map(upsell => upsell.id))
    }
  }, [dispatch, upsellProductGroups, upsellProducts])

  useEffect(() => {
    setFlyoutCartIsShowing(true)
    return () => {
      setFlyoutCartIsShowing(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const cartIsEmpty = lineItems.length === 0

  const handleTrackRemoveProduct = lineItem => {
    Hub.pub(Event.Navigation.REMOVE_PRODUCT_FROM_CART, {
      product_id: lineItem.product_id,
    })
  }

  const ProductUpsellCarouselItem = ({ product }) => {
    const {
      permalink,
      id: upsellProductId,
      main_image,
      display_name,
      variants,
      collection: { permalink: collectionPermalink } = {},
      color: { name: colorName },
    } = product

    const upsellPrice = productPrices.find(product => product.id === upsellProductId)
    const { converted_price: convertedPrice } = upsellPrice || {}

    // This code assumes that Upsells will only have one variant
    const orderableState = variants[0].orderable_state

    return permalink ? (
      <div className="upsell-product-carousel__item">
        <RouterLink
          href={`/products/${permalink}`}
          className="upsell-product__image-wrapper"
          onClick={() => closeCart()}
        >
          <img
            className="upsell-product__image"
            src={ImageHelper.imageUrl(main_image, {
              width: 72,
              height: 96,
            })}
            alt={`${display_name}`}
          />
        </RouterLink>
        <div className="upsell-product__details-container">
          <p className="upsell-product__title">{display_name}</p>
          <p className="upsell-product__details">{`One Size | ${colorName}`}</p>
          <br />
          <p className="upsell-product__price upsell-product__details--bolded">
            {convertedPrice ? `${currencySymbol}${convertedPrice.toFixed(0)}` : null}
          </p>
        </div>
        <div className="upsell-product__cta">
          <PurchaseButton
            onClick={() => {
              addToCart({
                product,
                variant: variants[0],
                collection: collectionPermalink,
              })
              Hub.pub(Event.Checkout.PRODUCT_UPSELL_ATC)
            }}
            className="upsell-product__cta-button"
            orderableState={orderableState}
            defaultText="Add"
          />
        </div>
      </div>
    ) : null
  }

  return (
    <Modal
      isFlyoutCart
      minHeight={600}
      width={477}
      containerClass="flyout-cart"
      backgroundClass="flyout-cart__background"
      onRequestClose={() => {
        closeCart()
        document.body.classList.remove('locked')
      }}
      overflowX="hidden"
      overflowY="hidden"
      zIndex={30}
      shouldFadeIn={false}
      useBaseModalClass={false}
    >
      <div className="flyout-cart__content">
        {!areLineItemsSyncing && cartIsEmpty ? (
          <BuilderComponent model="empty-cart-content" />
        ) : (
          <Fragment>
            <div className="flyout-cart__header-container">
              <ShippingProgressBar />
            </div>
            <div className="flyout-cart__scrollable-section">
              <h3 className="flyout-cart__header">Your Cart</h3>
              <Cart
                isFlyoutCart
                showBundles
                imageHeight={80}
                isSyncing={areLineItemsSyncing}
                changeLineItemQuantity={dispatchUpdateItemQuantity}
                onTrackRemoveProduct={handleTrackRemoveProduct}
              />

              <h3 className="flyout-cart__subheader">Before You Go</h3>
              {upsellProducts.length > 0 && hasUpsellProducts && (
                <Carousel
                  showDots
                  showArrows={false}
                  slidesToShow={1}
                  dotTheme="dark"
                  className="flyout-cart__upsells-carousel"
                >
                  {upsellProducts.map(product => (
                    <ProductUpsellCarouselItem key={product.display_name} product={product} />
                  ))}
                </Carousel>
              )}
            </div>

            <FlyoutCartSubtotal
              lineItems={lineItems}
              hasWelcomeOffer={hasWelcomeOffer}
              currencySymbol={currencySymbol}
              subTotalDecimal={subTotalDecimal}
              orderTotalDecimal={orderTotalDecimal}
              returnPolicy={returnPolicy}
              cartUrgencyMessagingEnabled={cartUrgencyMessagingEnabled}
            />
          </Fragment>
        )}
      </div>
    </Modal>
  )
}

function mapStateToProps(state) {
  const countryCode = getCountryCode()
  const orderPreview = state.checkout.orderPreview

  return {
    countryCode,
    orderPreview,
    lineItems: state.cart.line_items ?? [],
    areLineItemsSyncing: !!state.cart.isBlocked,
    shippingPolicy: state.session.shippingPolicy,
    upsellProductGroups: state.checkout.upsellProducts.upsellProductGroups ?? [],
    hasWelcomeOffer: state.session.visitor?.has_welcome_offer,
    currencySymbol: state.cart?.line_items?.[0]?.currency_symbol || '$',
    subTotalDecimal: parseFloat(orderPreview?.subtotal_decimal || 0),
    orderTotalDecimal: orderPreview ? orderPreview.total_decimal : null,
    productPrices: state.productPrices?.products || [],
    returnPolicy: state.regions && state.regions.returnPolicy,
    cartUrgencyMessagingEnabled: state.appConfig.cartUrgencyMessagingEnabled,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    addToCart: ({ product, variant, unit, collection }) => {
      return dispatch(
        addLineItem({
          collection,
          variant,
          unit,
          product,
        }),
      )
    },
    setFlyoutCartIsShowing: bool => {
      return dispatch(receiveShowingFlyoutCart(bool))
    },
  }
}

FlyoutCart.propTypes = {
  areLineItemsSyncing: PropTypes.bool,
  lineItems: PropTypes.array,
  addToCart: PropTypes.func,
  setFlyoutCartIsShowing: PropTypes.func,
  upsellProductGroups: PropTypes.array,
  hasWelcomeOffer: PropTypes.bool,
  currencySymbol: PropTypes.string,
  subTotalDecimal: PropTypes.number,
  orderTotalDecimal: PropTypes.number,
  closeCart: PropTypes.func,
}

FlyoutCartSubtotal.propTypes = {
  hasWelcomeOffer: PropTypes.bool,
  lineItems: PropTypes.array,
  currencySymbol: PropTypes.string,
  subTotalDecimal: PropTypes.number,
  orderTotalDecimal: PropTypes.number,
}

export default connect(mapStateToProps, mapDispatchToProps)(FlyoutCart)
